import { z } from 'zod';

/**
 * Queue-routing events — the worker's report on a job it could not hand to a
 * handler.
 *
 * Motivating defect (found 2026-09-03, fixed in the same change): both of the
 * worker's routers were `if / else if` chains with no `else`. A job whose name
 * matched none of the type guards fell straight through and returned
 * `undefined`, which every caller read as success:
 *
 *   - the checks worker logged `jobComplete` and acked the job to BullMQ;
 *   - the automations worker read `result?.status?.includes('SKIPPED')` as
 *     `false`, took the success branch, and wrote a **SUCCESS** `JobExecution`
 *     row for work that never ran.
 *
 * No throw, no retry, no Rollbar. The work vanished and the record said it
 * succeeded — the exact shape ADR 0002 exists to end.
 *
 * Two live paths can produce a name no handler knows:
 *
 *   1. **A partial rollback.** api and worker are separate Railway services on
 *      one Redis queue. Roll the worker back across a release that added a job
 *      name and the newer api keeps enqueueing it; rolling forward again does
 *      not replay what was already acked. See docs/rollback-runbook.md §1c.
 *   2. **Automation job names are built from church data.** `createChildren`
 *      composes `AUTO-${integrationSource}-${type}`, and `integrationSource` is
 *      a free-form column, not an enum. Anything the admin UI does not
 *      currently offer composes a name the worker has never heard of.
 *
 * The router now emits this and throws. One event per unroutable job is the
 * signal a monitor can alert on: in steady state this is silent, and any
 * volume at all means work is being refused.
 */

/** Which of the worker's two routers refused the job. */
export const QUEUE_ROUTERS = [
  /** `onark-queue-${NODE_ENV}-checks` — CheckForAutomations and friends. */
  'checks',
  /** `onark-queue-${NODE_ENV}-automations` — the `AUTO-*` child jobs. */
  'automations',
] as const;

export type QueueRouter = (typeof QUEUE_ROUTERS)[number];

export const queueSchemas = {
  /**
   * A job reached a router whose guards all declined it. The job is failed
   * rather than acked, so BullMQ retries it and then dead-letters it into the
   * `failed` set, where it can be replayed once the handler exists again.
   *
   * `jobName` is the property to group by — it names either the release the
   * worker is missing or the `integrationSource` value nobody built a handler
   * for.
   */
  'queue.job_unrouted': z.object({
    /** Absent when the payload carries no church (e.g. `AutomationFlow`). */
    churchId: z.string().optional(),
    /** BullMQ job id, for finding the row in the `failed` set. */
    jobId: z.string().optional(),
    /** The unrecognized `job.name`. */
    jobName: z.string(),
    /** Full BullMQ queue name, which carries the environment suffix. */
    queueName: z.string(),
    router: z.enum(QUEUE_ROUTERS),
  }),
  /**
   * The reconciliation sweep found a finished submission whose
   * `CheckForAutomations` never left a `JobExecution` row, and re-dispatched
   * it. In steady state this is silent: any volume means the hot-path enqueue
   * (or the Redis behind it) is losing dispatches, which is the failure this
   * sweep exists to catch. `ageMinutes` is how long the check-in went
   * unprocessed before the sweep noticed.
   */
  'queue.dispatch_reconciled': z.object({
    ageMinutes: z.number().int().nonnegative(),
    checkInId: z.string(),
    churchId: z.string(),
    sectionId: z.string(),
  }),
  /** The sweep found an unprocessed check-in but could not re-dispatch it. */
  'queue.dispatch_reconcile_failed': z.object({
    checkInId: z.string(),
    churchId: z.string(),
    message: z.string(),
    sectionId: z.string(),
  }),
  /**
   * The worker restarted itself because its resident memory crossed the
   * configured limit (`WORKER_MEMORY_RESTART_MB`).
   *
   * Motivating bill (2026-09-25): the production worker idles at 0.3 GB, climbs
   * to 3-7.5 GB over a few hours of check-in load and then holds that plateau
   * until the next deploy. Railway bills by resident memory per second, so the
   * worker alone was ~$20/month of a ~$88 bill. The guard sends itself SIGTERM
   * (graceful: queues pause, in-flight jobs finish) and Railway restarts it.
   *
   * Steady state is a few events a week. A burst of them means the limit is
   * below the worker's real working set and jobs are being interrupted.
   */
  'queue.worker_memory_restarted': z.object({
    heapUsedMb: z.number().int().nonnegative(),
    limitMb: z.number().int().positive(),
    rssMb: z.number().int().nonnegative(),
    uptimeSeconds: z.number().int().nonnegative(),
    /**
     * Off-heap breakdown and what was running. The 2026-10-05 fires had
     * heapUsed 90-180 MB against RSS 1.3-3.1 GB, so the growth is native
     * memory, and a restart loop (re-fire ~70s after boot) means the cause is
     * whatever the fresh process picks up first. These say which.
     */
    arrayBuffersMb: z.number().int().nonnegative().optional(),
    externalMb: z.number().int().nonnegative().optional(),
    /** `name:id` of jobs in flight when the guard fired (capped at 10). */
    activeJobs: z.array(z.string()).max(10).optional(),
  }),

  /**
   * A job reached a router with a `v` outside `SUPPORTED_PAYLOAD_VERSIONS`
   * (packages/shared/src/queue.ts). The job is failed rather than processed,
   * so BullMQ retries it and then dead-letters it into the `failed` set,
   * where it can be replayed once both services agree on a payload version.
   *
   * This is `queue.job_unrouted`'s sibling for a partial api/worker rollback
   * (docs/rollback-runbook.md §1c): a job name it knows but a payload shape
   * it doesn't.
   */
  'queue.job_version_rejected': z.object({
    /** BullMQ job id, for finding the row in the `failed` set. */
    jobId: z.string().optional(),
    /** The job name whose payload version was rejected. */
    jobName: z.string(),
    /** The `SUPPORTED_PAYLOAD_VERSIONS` this worker build accepts. */
    supported: z.array(z.number()),
    /** The rejected `job.data.v`. */
    version: z.number(),
  }),
} as const;
